import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center py-20">
          <i className="ri-error-warning-line text-5xl text-secondary-300 mb-4"></i>
          <p className="text-foreground-700 text-lg font-medium">文章未找到</p>
          <Link to="/" className="mt-4 text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);
  const cat = categoryMap[article.categorySlug];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料舍',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <article className="pt-20 pb-10">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {article.isExclusive && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-accent-500 text-background-50">独家</span>
              )}
              {article.isHot && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-500 text-background-50">热门</span>
              )}
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs text-primary-600 font-medium hover:text-primary-700 cursor-pointer"
              >
                {article.category}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-secondary-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 黑料舍独家爆料`}
              className="w-full aspect-[16/9] object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none mb-10
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="flex flex-wrap gap-2 mb-8 pt-6 border-t border-secondary-200/60">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between py-4 border-t border-secondary-200/60">
            <div className="flex items-center gap-3">
              <span className="text-sm text-secondary-500">分享到：</span>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-secondary-600 text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-secondary-600 text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-secondary-600 text-sm"></i>
              </a>
            </div>
            <button className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer">
              <i className="ri-chat-1-line"></i> 我要评论
            </button>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-lg font-bold font-heading text-foreground-900 mb-5 flex items-center gap-2">
              <i className="ri-links-line text-primary-500"></i>
              相关爆料
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.id} article={ra} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </div>
  );
}